import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { questionAPI, gameSessionAPI } from '../services/api';
import UserMenu from '../components/UserMenu';
import XpBreakdown from '../subjects/XpBreakdown';
import PersonalStatsSection from '../components/stats/PersonalStatsSection';
import PersonalBestsSection from '../components/stats/PersonalBestsSection';
import LeaderboardSection from '../components/stats/LeaderboardSection';

interface UserStats {
  total_questions_answered: number;
  correct_answers: number;
  accuracy_percentage: number;
  current_streak: number;
  current_rating: number;
  activity_by_date?: {
    date: string;
    questions_answered: number;
  }[];
  exam_completion?: {
    year: number;
    exam_type: number;
    question_types: Record<string, { total: number; correct: number }>;
  }[];
}

interface PersonalBest {
  score: number;
  date: string;
  duration_minutes?: number;
}

interface PersonalBests {
  survival_best: PersonalBest | null;
  rush_5_best: PersonalBest | null;
  rush_10_best: PersonalBest | null;
  rush_20_best: PersonalBest | null;
  rush_30_best: PersonalBest | null;
}

const StatsAndRankings: React.FC = () => {
  const { user } = useAuth();
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [personalBests, setPersonalBests] = useState<PersonalBests | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [activityTooltip, setActivityTooltip] = useState<{
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const [today, setToday] = useState(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [statsData, bestsData] = await Promise.all([
          questionAPI.getUserStats(),
          gameSessionAPI.getPersonalBests()
        ]);
        setUserStats(statsData);
        setPersonalBests(bestsData);
      } catch (error) {
        console.error('Failed to load stats data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0);
    const timeoutMs = nextMidnight.getTime() - now.getTime();
    const timeoutId = window.setTimeout(() => {
      const nextDay = new Date();
      nextDay.setHours(0, 0, 0, 0);
      setToday(nextDay);
    }, timeoutMs);
    return () => window.clearTimeout(timeoutId);
  }, [today]);

  const joinYear = useMemo(() => {
    if (user?.created_at) {
      const created = new Date(user.created_at);
      if (!Number.isNaN(created.getTime())) {
        return created.getFullYear();
      }
    }
    return today.getFullYear();
  }, [today, user?.created_at]);

  const availableYears = useMemo(() => {
    const currentYear = today.getFullYear();
    const years: number[] = [];
    for (let year = currentYear; year >= joinYear; year -= 1) {
      years.push(year);
    }
    return years;
  }, [joinYear, today]);

  useEffect(() => {
    if (selectedYear === null && availableYears.length > 0) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const formatLocalDate = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const activityData = useMemo(
    () => userStats?.activity_by_date ?? [],
    [userStats?.activity_by_date]
  );
  const activityMap = useMemo(() => {
    const map = new Map<string, number>();
    activityData.forEach((item) => {
      const date = new Date(`${item.date}T00:00:00Z`);
      const key = formatLocalDate(date);
      map.set(key, (map.get(key) ?? 0) + (item.questions_answered ?? 0));
    });
    return map;
  }, [activityData]);
  const rangeStart = useMemo(() => {
    const year = selectedYear ?? today.getFullYear();
    if (year === today.getFullYear()) {
      const value = new Date(today);
      value.setDate(value.getDate() - 364);
      value.setHours(0, 0, 0, 0);
      return value;
    }
    const value = new Date(year, 0, 1);
    value.setHours(0, 0, 0, 0);
    return value;
  }, [selectedYear, today]);
  const rangeEnd = useMemo(() => {
    const year = selectedYear ?? today.getFullYear();
    if (year === today.getFullYear()) {
      return today;
    }
    const value = new Date(year, 11, 31);
    value.setHours(0, 0, 0, 0);
    return value;
  }, [selectedYear, today]);

  const { weeks, maxQuestions, monthLabels } = useMemo(() => {
    const firstSunday = new Date(rangeStart);
    firstSunday.setDate(firstSunday.getDate() - firstSunday.getDay());
    const lastSaturday = new Date(rangeEnd);
    lastSaturday.setDate(lastSaturday.getDate() + (6 - lastSaturday.getDay()));
    const totalGridDays = Math.round(
      (lastSaturday.getTime() - firstSunday.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    const computedWeeks: Date[][] = [];
    const computedMonthLabels: string[] = [];
    let maxCount = 0;
    for (let i = 0; i < totalGridDays; i += 7) {
      const week: Date[] = [];
      let monthLabel = '';
      for (let d = 0; d < 7; d += 1) {
        const day = new Date(firstSunday);
        day.setDate(firstSunday.getDate() + i + d);
        week.push(day);
        if (day >= rangeStart && day <= rangeEnd) {
          const key = formatLocalDate(day);
          const count = activityMap.get(key) ?? 0;
          maxCount = Math.max(maxCount, count);
          if (day.getDate() === 1) {
            monthLabel = day.toLocaleString(undefined, { month: 'short' });
          }
        }
      }
      computedWeeks.push(week);
      computedMonthLabels.push(monthLabel);
    }

    return { weeks: computedWeeks, maxQuestions: maxCount, monthLabels: computedMonthLabels };
  }, [activityMap, rangeEnd, rangeStart]);

  const getActivityColor = (count: number) => {
    if (!count) return 'bg-gray-100 dark:bg-gray-700';
    if (count < Math.max(2, Math.ceil(maxQuestions * 0.25))) return 'bg-mango-200 dark:bg-mango-800';
    if (count < Math.max(4, Math.ceil(maxQuestions * 0.5))) return 'bg-mango-400 dark:bg-mango-600';
    return 'bg-mango-600 dark:bg-mango-500';
  };

  const questionTypeOrder: Record<string, string> = {
    MC: 'Multiple Choice',
    SA: 'Short Answer',
    ER: 'Extended Response',
  };
  const orderedTypes = ['MC', 'SA', 'ER'];

  const examCompletionByYear = useMemo(() => {
    const entries = userStats?.exam_completion ?? [];
    const map = new Map<number, { year: number; exams: Record<number, Record<string, { total: number; correct: number }>> }>();
    entries.forEach((exam) => {
      const yearEntry = map.get(exam.year) ?? { year: exam.year, exams: {} };
      yearEntry.exams[exam.exam_type] = exam.question_types ?? {};
      map.set(exam.year, yearEntry);
    });
    return Array.from(map.values()).sort((a, b) => b.year - a.year);
  }, [userStats?.exam_completion]);

  const showActivityTooltip = (
    event: React.MouseEvent<HTMLDivElement>,
    text: string
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 220;
    const tooltipHeight = 32;
    const padding = 8;
    const x = Math.min(rect.right + 10, window.innerWidth - tooltipWidth - padding);
    const y = Math.max(padding, Math.min(rect.top - 8, window.innerHeight - tooltipHeight - padding));
    setActivityTooltip({ text, x, y });
  };

  const hideActivityTooltip = () => {
    setActivityTooltip(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-10 w-10 bg-mango-500 rounded-lg flex items-center justify-center">
                <TrophyIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-3">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Stats & Rankings
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Your performance and global leaderboards
                </p>
              </div>
            </div>
            <UserMenu />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activityTooltip && (
          <div
            className="fixed z-50 rounded bg-gray-900 px-2 py-1 text-[10px] text-white shadow-md"
            style={{ left: activityTooltip.x, top: activityTooltip.y }}
          >
            {activityTooltip.text}
          </div>
        )}
        {/* Back to Dashboard Link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-mango-600 dark:hover:text-mango-400 transition-colors mb-6"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back to Dashboard
        </Link>

        <XpBreakdown />

        {/* Activity Tracker */}
        <div className="mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Question Activity</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Questions answered per day
                </p>
              </div>
            </div>
            <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
              {(() => {
                const streak = userStats?.current_streak ?? 0;
                if (streak <= 0) {
                  return 'Answer questions to start your daily streak';
                }
                if (streak <= 7) {
                  return `You're on a ${streak} day streak! Keep it up.`;
                }
                if (streak <= 14) {
                  return `You're on a ${streak} day streak! You're on a roll!`;
                }
                if (streak <= 30) {
                  return `You're on a ${streak} day streak! Your discipline is unmatched!`;
                }
                return `You're on a ${streak} day streak! Raw 50 incoming!`;
              })()}
            </div>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 overflow-x-auto">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8" />
                  <div className="grid grid-flow-col auto-cols-max gap-1 text-[10px] text-gray-400 dark:text-gray-500 pl-1">
                    {monthLabels.map((label, index) => (
                      <div key={`month-${index}`} className="w-3">
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-8 grid grid-rows-7 gap-1 text-[10px] text-gray-400 dark:text-gray-500 pt-[1px]">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, rowIndex) => (
                      <div key={label} className="h-3 flex items-center">
                        {rowIndex === 1 || rowIndex === 3 || rowIndex === 5 ? label : ''}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-flow-col auto-cols-max gap-1">
                    {weeks.map((week, weekIndex) => (
                      <div key={`week-${weekIndex}`} className="grid grid-rows-7 gap-1">
                        {week.map((day) => {
                          const key = formatLocalDate(day);
                          const inRange = day >= rangeStart && day <= rangeEnd;
                          const count = inRange ? (activityMap.get(key) ?? 0) : 0;
                          const tooltip = `${key}: ${count} question${count === 1 ? '' : 's'}`;
                          return (
                            <div
                              key={key}
                              aria-label={inRange ? tooltip : 'Outside range'}
                              onMouseEnter={(event) => inRange && showActivityTooltip(event, tooltip)}
                              onMouseLeave={hideActivityTooltip}
                              className={`h-3 w-3 rounded-sm ${
                                inRange ? getActivityColor(count) : 'bg-gray-50 dark:bg-gray-800'
                              }`}
                            />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex lg:flex-col flex-row gap-2 lg:items-start items-center">
                {availableYears.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => setSelectedYear(year)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      selectedYear === year
                        ? 'bg-mango-500 text-white'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>Less</span>
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3].map((level) => {
                  const sampleCount = level === 0 ? 0 : Math.max(1, Math.ceil(maxQuestions * (level / 3)));
                  return (
                    <span
                      key={level}
                      className={`h-3 w-3 rounded-sm ${getActivityColor(sampleCount)}`}
                    />
                  );
                })}
              </div>
              <span>More</span>
            </div>
          </div>
        </div>

        {/* Exam Completion Section */}
        <div className="mb-8">
          <details open className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <summary className="cursor-pointer list-none">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Exam Completion</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Correctly solved questions by exam and question type
                  </p>
                </div>
              </div>
            </summary>
            <div className="mt-4">
              {examCompletionByYear.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                  {examCompletionByYear.map((yearEntry) => (
                    <div
                      key={yearEntry.year}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {yearEntry.year} Exams
                        </span>
                      </div>
                      <div className="space-y-3">
                        {[1, 2].map((examType) => {
                          const examData = yearEntry.exams[examType];
                          if (!examData) return null;
                          const total = Object.values(examData).reduce((sum, type) => sum + type.total, 0);
                          return (
                            <div key={examType} className="space-y-2">
                              <div className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                Exam {examType} · {total} total
                              </div>
                              {orderedTypes.map((typeKey) => {
                                const typeData = examData[typeKey];
                                if (!typeData) return null;
                                const percentage = typeData.total > 0
                                  ? Math.round((typeData.correct / typeData.total) * 100)
                                  : 0;
                                return (
                                  <div key={`${examType}-${typeKey}`}>
                                    <div className="flex items-center justify-between text-[11px] text-gray-600 dark:text-gray-400 mb-1">
                                      <span>{questionTypeOrder[typeKey] ?? typeKey}</span>
                                      <span>{percentage}% ({typeData.correct}/{typeData.total})</span>
                                    </div>
                                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                      <div
                                        className="bg-mango-500 h-2 rounded-full"
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Start practicing to see your exam completion breakdown.
                </div>
              )}
            </div>
          </details>
        </div>

        {/* Personal Stats Section */}
        <div className="mb-8">
          <PersonalStatsSection
            userStats={userStats}
            isLoading={isLoading}
          />
        </div>

        {/* Personal Bests Section */}
        <div className="mb-8">
          <PersonalBestsSection
            personalBests={personalBests}
            isLoading={isLoading}
          />
        </div>

        {/* Leaderboard Section */}
        <div>
          <LeaderboardSection currentUserId={user?.id} />
        </div>
      </div>
    </div>
  );
};

export default StatsAndRankings;
